import React, { useCallback, useState, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import Gallery, { RenderImageProps, PhotoProps } from 'react-photo-gallery';
import ImageItem from './ImageItem';
import { tabImageState } from '../../../modules/tabImage';
import DetailViewMobile from './DetailViewMobile';
import { media } from '../../common/Layout';
import { PostConsumptionBodyType } from '../../../lib/common/type';

const ImageListComponent = styled.div`
  position: relative;
  width: 100%;
  .react-photo-gallery--gallery {
    /* Mobile Device */
    img {
      border-radius: 6px;
    }

    ${media.desktop`
      border-radius: 6px;
    `};
  }
`;

interface CustomPhotoProps {
  id?: number;
  url?: string;
  src: string;
  width: number;
  height: number;
  targetId?: number;
  alt?: string | undefined;
  title?: string;
  desc?: string;
  source?: string;
}

export interface photosType {
  index: number;
  next: PhotoProps<CustomPhotoProps> | null;
  photo: PhotoProps<CustomPhotoProps>;
  previous: PhotoProps<CustomPhotoProps> | null;
}

type ImageListItemProps = {
  photoData: {
    id: number;
    url: string;
    src: string;
    width: number;
    height: number;
    targetId?: number;
    alt?: string | undefined;
    title?: string;
    desc?: string;
    source?: string;
  }[];
  onSetSelectPhotoData: (
    selectPhotoData: tabImageState['selectPhotoData']
  ) => void;
  onSetIsDetailViewOpen: (
    isDetailViewOpen: tabImageState['isDetailViewOpen']
  ) => void;
  onPostConsumption: (info: PostConsumptionBodyType) => void;
  selectPhotoData: tabImageState['selectPhotoData'];
};

const ImageList = ({
  photoData,
  selectPhotoData,
  onSetSelectPhotoData,
  onSetIsDetailViewOpen,
  onPostConsumption,
}: ImageListItemProps) => {
  const [innerWidth, setInnerWidth] = useState(window.innerWidth);
  const [isDetailViewMobile, setIsDetailViewMobile] = useState(false);
  const [selectPhoto, setSelectPhoto] = useState<photosType['photo'] | null>();
  const [columns, setColumns] = useState(2);

  useEffect(() => {
    setInnerWidth(window.innerWidth);
    if (window.innerWidth < 500) {
      setColumns(2);
    } else if (window.innerWidth < 900) {
      setColumns(3);
    } else if (window.innerWidth < 1100) {
      setColumns(4);
    }
    window.addEventListener('resize', () => {
      setInnerWidth(window.innerWidth);
      if (window.innerWidth < 500) {
        setColumns(2);
      } else if (window.innerWidth < 900) {
        setColumns(3);
      } else if (window.innerWidth < 1100) {
        setColumns(4);
      }
    });
  }, []);

  useEffect(() => {
    function handleTouchMove(event: any) {
      if (isDetailViewMobile) {
        event.preventDefault(); // 여기가 핵심
      }
    }
    window.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });
    return () => window.removeEventListener('touchmove', handleTouchMove);
  }, [isDetailViewMobile]);

  const imageRenderer = useCallback(
    ({
      left,
      top,
      photo,
      onClick,
      direction,
      margin,
      index,
    }: RenderImageProps) => {
      const tempPhoto: {
        id?: number;
        url?: string;
        src: string;
        width: number;
        height: number;
        targetId?: number;
        alt?: string | undefined;
        title?: string;
        desc?: string;
        source?: string;
      } = photo;

      return (
        <ImageItem
          margin={margin}
          index={index}
          photo={tempPhoto}
          left={left}
          top={top}
          onClick={onClick}
          direction={direction}
          key={index}
          onSetIsDetailViewOpen={onSetIsDetailViewOpen}
          onSetSelectPhotoData={onSetSelectPhotoData}
          onPostConsumption={onPostConsumption}
          selectPhotoData={selectPhotoData}
        />
      );
    },
    [onSetSelectPhotoData, onSetIsDetailViewOpen, selectPhotoData]
  );

  const onClick = (event: React.MouseEvent, photos: photosType) => {
    setSelectPhoto(photos.photo);
    setIsDetailViewMobile(true);
  };

  return (
    <ImageListComponent>
      {innerWidth < 1100 ? (
        <>
          <Gallery
            photos={photoData}
            direction="column"
            margin={3}
            columns={columns}
            onClick={onClick}
          />
          {isDetailViewMobile ? (
            <DetailViewMobile
              setIsDetailViewMobile={setIsDetailViewMobile}
              selectPhoto={selectPhoto}
            />
          ) : null}
        </>
      ) : (
        <Gallery
          photos={photoData}
          renderImage={imageRenderer}
          targetRowHeight={250}
          limitNodeSearch={10}
          margin={3}
        />
      )}
    </ImageListComponent>
  );
};

export default ImageList;
